import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { FieldsetModule } from 'primeng/fieldset';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { finalize, forkJoin } from 'rxjs';
import { CrmAccount, CrmClient } from '../../crm/crm.model';
import { CrmService } from '../../crm/crm.service';
import { LogisticsProjectsService } from '../../logistics-projects/logistics-projects.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { PermissionService } from '../../security/permission.service';
import { app_permissions } from '../../security/app-permissions';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { LazyLocationPickerDirective } from '../../shared/location-picker/lazy-location-picker.directive';
import { WarehousingMasterType, masterDataMap, warehousingBreadcrumb, warehousingHomeBreadcrumb } from '../warehousing-common';
import { WarehousingGoodsOutAvailability, WarehousingItem, WarehousingManualGoodsOutCommand, WarehousingMasterData } from '../warehousing.model';
import { WarehousingService } from '../warehousing.service';
import { itemSizeLabel } from '../warehousing-item-display';

@Component({
  selector: 'app-warehousing-dispatch-form',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, RouterModule, BreadcrumbModule, ButtonModule, CardModule, DialogModule, FieldsetModule,
    IftaLabelModule, InputGroupModule, InputGroupAddonModule, InputNumberModule, InputTextModule, ProgressBarModule,
    MessageModule, PageFormSectionComponent, PageHeroComponent, PageShellComponent, LazyLocationPickerDirective,
    SelectModule, TableModule, TextareaModule, ToastModule
  ],
  providers: [MessageService],
  templateUrl: './warehousing-dispatch-form.component.html'
})
export class WarehousingDispatchFormComponent implements OnInit {
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private warehousingService = inject(WarehousingService);
  private crmService = inject(CrmService);
  private logisticsProjectsService = inject(LogisticsProjectsService);
  private sessionStorageService = inject(SessionStorageService);
  private permissionService = inject(PermissionService);

  readonly isGoodsOutMode = this.route.snapshot.data['mode'] === 'GOODS_OUT';
  readonly canPostGoodsOut = this.permissionService.hasPermission(app_permissions.warehousing.dispatch.release);
  readonly locationCompanyId = this.sessionStorageService.getCompany()?.id
    || this.sessionStorageService.getCustomer()?.company?.id || 0;
  private readonly goodsOutKey = crypto.randomUUID();
  reviewOpen = false;
  isCheckingAvailability = false;
  availabilityError: string | null = null;
  availabilityByItem = new Map<number, WarehousingGoodsOutAvailability[]>();
  reviewAvailabilityReady = false;
  private availabilityGeneration = 0;
  private availabilityRefreshScheduled = false;

  isLoading = false;
  isSaving = false;
  submitError: string | null = null;
  breadcrumbItems: MenuItem[] = warehousingBreadcrumb('New Dispatch', 'Dispatches', '/warehousing/dispatch');
  homeBreadcrumb: MenuItem = warehousingHomeBreadcrumb();
  clients: CrmClient[] = [];
  accounts: CrmAccount[] = [];
  items: WarehousingItem[] = [];
  masterData = masterDataMap();
  dispatchSourceOptions = [
    { label: 'Manual', value: 'MANUAL' },
    { label: 'POSM Order Release', value: 'POSM_ORDER_RELEASE' },
    { label: 'Sales Order', value: 'SALES_ORDER' }
  ];

  dispatchForm = this.fb.group({
    referenceNumber: [''],
    sourceType: ['MANUAL', Validators.required],
    sourceNumber: [''],
    crmClientId: [null as number | null, Validators.required],
    crmAccountId: [null as number | null, Validators.required],
    warehouseId: [null as number | null],
    scheduledDate: [this.today()],
    deliveryAddress: [''],
    deliveryLocationId: [null as number | null],
    notes: [''],
    lines: this.fb.array([this.createLineGroup()])
  });

  get lines(): FormArray {
    return this.dispatchForm.get('lines') as FormArray;
  }

  itemOptions(): (WarehousingItem & { displayLabel: string })[] {
    const clientId = this.dispatchForm.value.crmClientId;
    const accountId = this.dispatchForm.value.crmAccountId;
    return this.items.filter(item => !this.isGoodsOutMode || ((!clientId || item.crmClientId === clientId)
      && (!accountId || item.crmAccountId === accountId))).map(item => ({
      ...item,
      displayLabel: [item.itemNumber, item.itemName, itemSizeLabel(item)].filter(Boolean).join(' | ')
    }));
  }

  ngOnInit(): void {
    if (this.isGoodsOutMode) {
      this.dispatchForm.controls.crmClientId.clearValidators();
      this.dispatchForm.controls.crmAccountId.clearValidators();
      this.dispatchForm.controls.warehouseId.setValidators(Validators.required);
      this.dispatchForm.controls.crmClientId.updateValueAndValidity();
      this.dispatchForm.controls.crmAccountId.updateValueAndValidity();
      this.dispatchForm.controls.warehouseId.updateValueAndValidity();
      this.dispatchForm.controls.warehouseId.valueChanges.subscribe(() => {
        this.lines.controls.forEach(line => line.patchValue({ zoneId: null, conditionId: null }, { emitEvent: false }));
        this.scheduleAvailabilityRefresh();
      });
      this.dispatchForm.controls.crmClientId.valueChanges.subscribe(clientId => {
        this.lines.controls.forEach(line => line.patchValue({ zoneId: null, conditionId: null }, { emitEvent: false }));
        const accounts = this.accounts.filter(account => account.crmClientId === clientId);
        this.dispatchForm.controls.crmAccountId.setValue(accounts.length === 1 ? accounts[0].id || null : null);
        this.scheduleAvailabilityRefresh();
      });
      this.dispatchForm.controls.crmAccountId.valueChanges.subscribe(() => {
        this.lines.controls.forEach(line => line.patchValue({ zoneId: null, conditionId: null }, { emitEvent: false }));
        this.scheduleAvailabilityRefresh();
      });
    }
    this.loadInitialData();
  }

  loadInitialData(): void {
    this.isLoading = true;
    forkJoin({
      clients: this.crmService.getClients(),
      accounts: this.crmService.getAccounts(),
      items: this.warehousingService.getItems(),
      regions: this.warehousingService.getMasterData('REGION'),
      warehouses: this.warehousingService.getMasterData('WAREHOUSE'),
      zones: this.warehousingService.getMasterData('ZONE'),
      conditions: this.warehousingService.getMasterData('ITEM_CONDITION')
    }).subscribe({
      next: response => {
        this.clients = response.clients || [];
        this.accounts = response.accounts || [];
        this.items = response.items.data || [];
        this.masterData.REGION = response.regions.data || [];
        this.masterData.WAREHOUSE = this.companyWarehouses(response.warehouses.data || []);
        this.masterData.ZONE = response.zones.data || [];
        this.masterData.ITEM_CONDITION = response.conditions.data || [];
        if (this.isGoodsOutMode && this.masterData.WAREHOUSE.length === 1) {
          this.dispatchForm.controls.warehouseId.setValue(this.masterData.WAREHOUSE[0].id || null);
        }
        this.lines.controls.forEach(control => this.reconcileLineHierarchy(control as FormGroup));
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.showSubmitError('Dispatch options could not be loaded. Refresh the page and try again.', 'Dispatch options unavailable');
      }
    });
  }

  addLine(): void {
    this.lines.push(this.createLineGroup());
  }

  removeLine(index: number): void {
    if (this.lines.length > 1) this.lines.removeAt(index);
  }

  createDispatch(): void {
    if (this.isGoodsOutMode) {
      this.reviewGoodsOut();
      return;
    }
    if (this.isSaving) {
      return;
    }
    if (this.dispatchForm.invalid) {
      this.dispatchForm.markAllAsTouched();
      this.showSubmitError(this.validationMessage());
      return;
    }
    this.submitError = null;
    const value = this.dispatchForm.getRawValue();
    this.isSaving = true;
    this.warehousingService.createDispatch({
      referenceNumber: value.referenceNumber || undefined,
      sourceType: value.sourceType || 'MANUAL',
      sourceNumber: value.sourceNumber || undefined,
      crmClientId: value.crmClientId!,
      crmAccountId: value.crmAccountId!,
      scheduledDate: value.scheduledDate || undefined,
      deliveryAddress: value.deliveryAddress || undefined,
      notes: value.notes || undefined,
      lines: (value.lines || []).map(line => ({
        itemId: line['itemId']!,
        quantityRequested: line['quantityRequested']!,
        transactionQuantity: line['transactionUomId'] ? line['quantityRequested']! : undefined,
        transactionUomId: line['transactionUomId'] || undefined,
        regionId: line['regionId'] || undefined,
        warehouseId: line['warehouseId'] || undefined,
        zoneId: line['zoneId'] || undefined,
        conditionId: line['conditionId'] || undefined
      }))
    }).pipe(finalize(() => this.isSaving = false)).subscribe({
      next: resp => {
        if (!resp.success) {
          this.showSubmitError(resp.message || 'The dispatch could not be created. Please try again.');
          return;
        }
        if (!resp.data?.id) {
          this.showSubmitError('The server did not confirm the new dispatch. Refresh the dispatch list before trying again.');
          return;
        }
        this.messageService.add({
          severity: 'success',
          summary: 'Dispatch Created',
          detail: resp.data.dispatchNumber ? `${resp.data.dispatchNumber} was created.` : 'Dispatch was created.'
        });
        this.linkProjectRequirementFulfillment(resp.data.id, resp.data.dispatchNumber)
          .add(() => this.router.navigate(['/warehousing/dispatch/view', resp.data.id]));
      },
      error: (error: HttpErrorResponse) => this.showSubmitError(this.dispatchErrorMessage(error))
    });
  }

  private reviewGoodsOut(): void {
    if (this.isSaving || !this.canPostGoodsOut) return;
    if (this.dispatchForm.invalid) {
      this.dispatchForm.markAllAsTouched();
      this.showSubmitError(this.validationMessage(), 'Goods out is incomplete');
      return;
    }
    for (let index = 0; index < this.lines.length; index++) {
      const value = this.lines.at(index).getRawValue();
      const item = this.items.find(row => row.id === value.itemId);
      if (!item) {
        this.showSubmitError(`Select an item on line ${index + 1}.`);
        return;
      }
      if (item.stockOwnerType !== 'TENANT_OWNED'
          && (!this.dispatchForm.value.crmClientId || !this.dispatchForm.value.crmAccountId)) {
        this.showSubmitError('Select the stock-owning client and account.');
        return;
      }
      if (this.itemRequiresUnitTracking(index)) {
        const baseQuantity = this.baseQuantityForLine(index);
        if (!Number.isInteger(baseQuantity) || this.unitSkus(index).length !== baseQuantity) {
          this.showSubmitError(`Enter one unique unit SKU for each base unit on line ${index + 1}.`);
          return;
        }
      }
    }
    this.submitError = null;
    this.reviewOpen = true;
    this.refreshGoodsOutAvailability(false, true);
  }

  postGoodsOut(): void {
    if (this.isSaving || !this.canPostGoodsOut || !this.canConfirmGoodsOut()) return;
    const value = this.dispatchForm.getRawValue();
    const command: WarehousingManualGoodsOutCommand = {
      idempotencyKey: this.goodsOutKey,
      warehouseId: value.warehouseId!,
      crmClientId: value.crmClientId || undefined,
      crmAccountId: value.crmAccountId || undefined,
      referenceNumber: value.referenceNumber || undefined,
      deliveryLocationId: value.deliveryLocationId || undefined,
      notes: value.notes || undefined,
      lines: value.lines.map((line, index) => ({
        itemId: line['itemId']!,
        quantity: line['quantityRequested']!,
        transactionUomId: line['transactionUomId'] || undefined,
        zoneId: line['zoneId'] || undefined,
        conditionId: line['conditionId'] || undefined,
        unitSkus: this.itemRequiresUnitTracking(index) ? this.unitSkus(index) : undefined
      }))
    };
    this.isSaving = true;
    this.warehousingService.createManualGoodsOut(command).pipe(finalize(() => this.isSaving = false)).subscribe({
      next: response => {
        if (!response.success || !response.data?.id) {
          this.showSubmitError(response.message || 'Goods out was not confirmed.');
          return;
        }
        this.reviewOpen = false;
        this.router.navigate(['/warehousing/dispatch/view', response.data.id]);
      },
      error: (error: HttpErrorResponse) => {
        this.showSubmitError(this.dispatchErrorMessage(error), 'Goods out failed');
        if (error.status === 409) this.refreshGoodsOutAvailability(false, true);
      }
    });
  }

  itemRequiresUnitTracking(index: number): boolean {
    const itemId = this.lineGroupAt(index).get('itemId')?.value;
    const item = this.items.find(row => row.id === itemId);
    return item?.trackingMode === 'UNIT' || item?.trackingMode === 'UNIQUE_ASSET' || item?.isDisplayStand === true;
  }

  unitSkus(index: number): string[] {
    return String(this.lineGroupAt(index).get('unitSkusText')?.value || '')
      .split(/[\n,]+/).map(code => code.trim()).filter(Boolean);
  }

  availableForLine(index: number): number {
    const line = this.lineGroupAt(index).getRawValue();
    return this.availabilityRowsForLine(index).filter(row => row.warehouseId === this.dispatchForm.value.warehouseId
      && (!line.zoneId || row.zoneId === line.zoneId)
      && (!line.conditionId || row.conditionId === line.conditionId))
      .reduce((sum, row) => sum + (row.availableQuantity || 0), 0);
  }

  availabilityRowsForLine(index: number): WarehousingGoodsOutAvailability[] {
    return this.availabilityByItem.get(this.lineGroupAt(index).get('itemId')?.value) || [];
  }

  localAvailability(index: number): WarehousingGoodsOutAvailability[] {
    return this.availabilityRowsForLine(index).filter(row => row.warehouseId === this.dispatchForm.value.warehouseId);
  }

  otherWarehouseAvailability(index: number): WarehousingGoodsOutAvailability[] {
    return this.localAvailability(index).length ? [] : this.availabilityRowsForLine(index)
      .filter(row => row.warehouseId !== this.dispatchForm.value.warehouseId);
  }

  useGoodsOutLocation(index: number, location: WarehousingGoodsOutAvailability): void {
    if (location.warehouseId !== this.dispatchForm.value.warehouseId) return;
    this.lineGroupAt(index).patchValue({ zoneId: location.zoneId, conditionId: location.conditionId });
  }

  retryGoodsOutAvailability(): void {
    this.refreshGoodsOutAvailability(false, true);
  }

  canConfirmGoodsOut(): boolean {
    if (this.isCheckingAvailability || this.availabilityError || !this.reviewAvailabilityReady) return false;
    const samePlacement = new Map<string, { requested: number; available: number }>();
    const itemTotals = new Map<number, { requested: number; available: number }>();
    for (let index = 0; index < this.lines.length; index++) {
      const line = this.lineGroupAt(index).getRawValue();
      const requested = this.baseQuantityForLine(index);
      const key = `${line.itemId}:${line.zoneId || '*'}:${line.conditionId || '*'}`;
      const placement = samePlacement.get(key) || { requested: 0, available: this.availableForLine(index) };
      placement.requested += requested;
      samePlacement.set(key, placement);
      const item = itemTotals.get(line.itemId) || {
        requested: 0,
        available: this.localAvailability(index).reduce((sum, row) => sum + row.availableQuantity, 0)
      };
      item.requested += requested;
      itemTotals.set(line.itemId, item);
    }
    return [...samePlacement.values(), ...itemTotals.values()]
      .every(row => row.requested <= row.available + 0.000001);
  }

  private scheduleAvailabilityRefresh(): void {
    if (!this.isGoodsOutMode || this.availabilityRefreshScheduled) return;
    this.availabilityRefreshScheduled = true;
    queueMicrotask(() => {
      this.availabilityRefreshScheduled = false;
      this.refreshGoodsOutAvailability(true);
    });
  }

  private refreshGoodsOutAvailability(autoSelect: boolean, forReview = false): void {
    if (!this.isGoodsOutMode) return;
    const generation = ++this.availabilityGeneration;
    const itemIds = [...new Set(this.lines.controls.map(line => line.get('itemId')?.value as number | null))]
      .filter((id): id is number => !!id);
    this.availabilityByItem = new Map();
    this.availabilityError = null;
    this.reviewAvailabilityReady = false;
    this.isCheckingAvailability = itemIds.length > 0;
    if (!itemIds.length) return;
    const clientId = this.dispatchForm.value.crmClientId || undefined;
    const accountId = this.dispatchForm.value.crmAccountId || undefined;
    if (new Set(itemIds.map(id => this.items.find(item => item.id === id)?.stockOwnerType || 'CRM_CLIENT_OWNED')).size > 1) {
      this.isCheckingAvailability = false;
      this.availabilityError = 'Goods out lines must share one stock owner. Use separate notes for tenant and client stock.';
      return;
    }
    if (itemIds.some(id => this.items.find(item => item.id === id)?.stockOwnerType !== 'TENANT_OWNED'
        && (!clientId || !accountId))) {
      this.isCheckingAvailability = false;
      this.availabilityError = 'Choose the stock-owning client and account to check availability.';
      return;
    }
    if (itemIds.some(id => {
      const item = this.items.find(row => row.id === id);
      return item?.stockOwnerType !== 'TENANT_OWNED'
        && (item?.crmClientId != null && item.crmClientId !== clientId
          || item?.crmAccountId != null && item.crmAccountId !== accountId);
    })) {
      this.isCheckingAvailability = false;
      this.availabilityError = 'A selected item belongs to another client or account. Choose matching ownership.';
      return;
    }
    forkJoin(itemIds.map(id => this.warehousingService.getGoodsOutAvailability(id, clientId, accountId)))
      .subscribe({
        next: responses => {
          if (generation !== this.availabilityGeneration) return;
          if (responses.some(response => !response.success || !Array.isArray(response.data))) {
            this.availabilityError = 'Stock availability could not be checked. Try again.';
          } else {
            this.availabilityByItem = new Map(itemIds.map((id, index) => [id, responses[index].data || []]));
            if (autoSelect) this.lines.controls.forEach((_, index) => {
              const line = this.lineGroupAt(index);
              const locations = this.localAvailability(index);
              if (!line.value.zoneId && !line.value.conditionId && locations.length === 1) {
                line.patchValue({ zoneId: locations[0].zoneId, conditionId: locations[0].conditionId });
              }
            });
            this.reviewAvailabilityReady = forReview;
          }
          this.isCheckingAvailability = false;
        },
        error: () => {
          if (generation !== this.availabilityGeneration) return;
          this.availabilityError = 'Stock availability could not be checked. Try again.';
          this.isCheckingAvailability = false;
        }
      });
  }

  baseQuantityForLine(index: number): number {
    const line = this.lineGroupAt(index).getRawValue();
    const item = this.items.find(row => row.id === line.itemId);
    const uomId = line.transactionUomId;
    const factor = uomId && uomId !== item?.baseUomId && uomId !== item?.tenantUomId
      ? item?.uomConversions?.find(row => row.uomId === uomId)?.factorToBase || 1 : 1;
    return (line.quantityRequested || 0) * factor;
  }

  itemName(itemId: number | null): string {
    const item = this.items.find(row => row.id === itemId);
    return item ? `${item.itemNumber || ''} ${item.itemName}`.trim() : '-';
  }

  goodsOutWarehouse(): string {
    return this.masterData.WAREHOUSE.find(row => row.id === this.dispatchForm.value.warehouseId)?.name || '-';
  }

  private showSubmitError(message: string, summary = 'Dispatch not created'): void {
    this.submitError = message;
    this.messageService.add({ severity: 'error', summary, detail: message, life: 10000 });
  }

  private validationMessage(): string {
    const missing: string[] = [];
    if (this.dispatchForm.get('crmClientId')?.invalid) missing.push('CRM client');
    if (this.dispatchForm.get('crmAccountId')?.invalid) missing.push('client account');
    if (this.dispatchForm.get('warehouseId')?.invalid) missing.push('warehouse');
    this.lines.controls.forEach((line, index) => {
      if (line.get('itemId')?.invalid) missing.push(`item on line ${index + 1}`);
      if (line.get('quantityRequested')?.invalid) missing.push(`positive quantity on line ${index + 1}`);
    });
    return missing.length
      ? `Complete the required fields: ${missing.join(', ')}.`
      : 'Check the highlighted fields before creating the dispatch.';
  }

  private dispatchErrorMessage(error: HttpErrorResponse): string {
    if (error.status === 0) {
      return 'Could not reach the server. Check your connection and try again.';
    }
    const message = error.error?.message;
    return typeof message === 'string' && message.trim()
      ? message.trim()
      : 'The dispatch could not be created. Please try again or contact support.';
  }

  discard(): void {
    this.router.navigate(['/warehousing/dispatch']);
  }

  filteredAccounts(clientId: number | null | undefined): CrmAccount[] {
    return this.accounts.filter(account => !clientId || !account.crmClientId || account.crmClientId === clientId);
  }

  options(type: WarehousingMasterType): WarehousingMasterData[] {
    return this.masterData[type] || [];
  }

  warehouseOptionsForLine(index: number): WarehousingMasterData[] {
    const regionId = this.lineGroupAt(index).get('regionId')?.value as number | null;
    return regionId
      ? this.masterData.WAREHOUSE.filter(row => row.parentId === regionId)
      : [];
  }

  zoneOptionsForLine(index: number): WarehousingMasterData[] {
    const warehouseId = this.isGoodsOutMode ? this.dispatchForm.value.warehouseId
      : this.lineGroupAt(index).get('warehouseId')?.value as number | null;
    return warehouseId
      ? this.masterData.ZONE.filter(row => row.parentId === warehouseId)
      : [];
  }

  isWarehouseSelectionDisabled(index: number): boolean {
    return !this.lineGroupAt(index).get('regionId')?.value;
  }

  isZoneSelectionDisabled(index: number): boolean {
    return this.isGoodsOutMode ? !this.dispatchForm.value.warehouseId
      : !this.lineGroupAt(index).get('warehouseId')?.value;
  }

  uomOptionsForLine(index: number): Array<{ label: string; value: number }> {
    const itemId = this.lineGroupAt(index).get('itemId')?.value as number | null;
    const item = this.items.find(row => row.id === itemId);
    if (!item?.tenantUomId) return [];
    const base = [{ label: item.tenantUomName || 'Base UOM', value: item.baseUomId || item.tenantUomId }];
    const conversions = (item.uomConversions || [])
      .filter(row => row.active !== false && row.uomId !== (item.baseUomId || item.tenantUomId))
      .map(row => ({ label: `${row.packLabel || 'UOM'} (${row.factorToBase} base)`, value: row.uomId }));
    return [...base, ...conversions];
  }

  private createLineGroup(): FormGroup {
    const group = this.fb.group({
      itemId: [null as number | null, Validators.required],
      quantityRequested: [1, [Validators.required, Validators.min(0.0001)]],
      transactionUomId: [null as number | null],
      regionId: [null as number | null],
      warehouseId: [null as number | null],
      zoneId: [null as number | null],
      conditionId: [null as number | null],
      unitSkusText: ['']
    });
    this.registerLineDependencies(group);
    return group;
  }

  private today(): string {
    return new Date().toISOString().slice(0, 10);
  }

  private lineGroupAt(index: number): FormGroup {
    return this.lines.at(index) as FormGroup;
  }

  private registerLineDependencies(lineGroup: FormGroup): void {
    lineGroup.get('regionId')?.valueChanges.subscribe(() => this.reconcileLineHierarchy(lineGroup));
    lineGroup.get('warehouseId')?.valueChanges.subscribe(() => this.reconcileLineHierarchy(lineGroup));
    lineGroup.get('itemId')?.valueChanges.subscribe(itemId => {
      if (!this.isGoodsOutMode) return;
      lineGroup.patchValue({ zoneId: null, conditionId: null }, { emitEvent: false });
      const item = this.items.find(row => row.id === itemId);
      if (item?.stockOwnerType !== 'TENANT_OWNED' && item?.crmClientId
          && !this.dispatchForm.controls.crmClientId.value) {
        this.dispatchForm.controls.crmClientId.setValue(item.crmClientId);
      }
      if (item?.stockOwnerType !== 'TENANT_OWNED' && item?.crmAccountId
          && !this.dispatchForm.controls.crmAccountId.value) {
        this.dispatchForm.controls.crmAccountId.setValue(item.crmAccountId);
      }
      this.scheduleAvailabilityRefresh();
    });
  }

  private reconcileLineHierarchy(lineGroup: FormGroup): void {
    const regionId = lineGroup.get('regionId')?.value as number | null;
    const warehouseId = lineGroup.get('warehouseId')?.value as number | null;
    const zoneId = lineGroup.get('zoneId')?.value as number | null;

    const warehouseStillValid = !!warehouseId
      && this.masterData.WAREHOUSE.some(row => row.id === warehouseId && row.parentId === regionId);
    if (warehouseId && !warehouseStillValid) {
      lineGroup.patchValue({ warehouseId: null, zoneId: null }, { emitEvent: false });
      return;
    }

    const zoneStillValid = !!zoneId
      && this.masterData.ZONE.some(row => row.id === zoneId && row.parentId === warehouseId);
    if (zoneId && !zoneStillValid) {
      lineGroup.patchValue({ zoneId: null }, { emitEvent: false });
    }
  }

  private companyWarehouses(rows: WarehousingMasterData[]): WarehousingMasterData[] {
    return (rows || []).filter(row => row.warehouseOwnershipType !== 'THIRD_PARTY_VIRTUAL');
  }

  private linkProjectRequirementFulfillment(targetResourceId?: number, sourceReference?: string) {
    const query = this.route.snapshot.queryParamMap;
    const projectId = Number(query.get('projectId') || query.get('logisticsProjectId') || 0);
    const requirementId = Number(query.get('requirementId') || 0);
    const owningCompanyId = this.sessionStorageService.getCompany()?.id || this.sessionStorageService.getCustomer()?.company?.id || 0;
    if (!projectId || !requirementId || !owningCompanyId || !targetResourceId) {
      return { add: (callback: () => void) => callback() };
    }
    const requestedQuantity = (this.dispatchForm.value.lines || [])
      .reduce((sum: number, line: any) => sum + (Number(line?.quantityRequested) || 0), 0);
    const queryQuantity = Number(query.get('quantity') || 0);
    const fulfilledQuantity = queryQuantity > 0 ? queryQuantity : (requestedQuantity > 0 ? requestedQuantity : 1);
    return this.logisticsProjectsService.linkRequirementFulfillment(projectId, owningCompanyId, requirementId, {
      targetResourceType: query.get('targetResourceType') || 'WAREHOUSING_DISPATCH',
      targetResourceId,
      sourceModule: 'WAREHOUSING',
      sourceReference: sourceReference || this.dispatchForm.value.referenceNumber || `Dispatch #${targetResourceId}`,
      fulfilledQuantity,
      fulfillmentStatus: 'CREATED',
      status: 'ACTIVE'
    }).subscribe({
      error: () => this.messageService.add({
        severity: 'warn',
        summary: 'Requirement link',
        detail: 'Dispatch was saved, but the requirement fulfillment link could not be updated.'
      })
    });
  }
}
